/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useState} from 'react';
import {ColDef} from 'ag-grid-community';
import {ColumnWithOrderKey} from './types';
import {getColumnWithOrderKey} from './utils';

/**
 * @useUserColumnViews
 *
 * Provides a Hook which returns selected columns and allows for manage enabling and disabling 
 * the Save As tab based on the selected column length. This hook returns isSaveAsDisabled and
 * selectedColumns state, which is called inside the SavedColumnContainer component.
 *
 * ```typescript
 *
 *     const {
 *       isSaveAsDisabled,
 *       selectedColumns
 *     } = useUserColumnViews();
 *
 *     ...
 *
 *    <TertiaryButton ...
 *      disabled={isSaveAsDisabled}
 *     ... >
 *      {translate('common:columnChooser.saveAs')}
 *    </TertiaryButton>
 *
 * ```
 */
export const useUserColumnViews = (definitions: ColDef[]) => {
  const columnWithOrder                         = getColumnWithOrderKey(definitions ?? []);
  const [selectedColumns, setSelectedColumns]   = useState<ColumnWithOrderKey[] | null>(columnWithOrder);
  const [isSaveAsDisabled, setIsSaveAsDisabled] = useState(false);

  useEffect(() => {
    if (definitions?.length) {
      const columnWithOrder = getColumnWithOrderKey(definitions);
      const checkedColumns = columnWithOrder.filter((column: ColumnWithOrderKey) => column?.hide === false);
      if (checkedColumns?.length) {
        setSelectedColumns(checkedColumns);
        setIsSaveAsDisabled(false);
      } else {
        setIsSaveAsDisabled(true);
      }
    }
  }, [definitions]);

  return {
    isSaveAsDisabled,
    selectedColumns
  };
};

export default useUserColumnViews;
